import "./NewSidebar.css";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import logo_black from "../../../assets/images/logo_black.svg";
import Cross from "../../../assets/images/cancelBlack.svg";
import Profile from "../../../assets/images/Profile.svg";
import {
  globalCurrentPageTitle,
} from "../../../store/globalstore";
import { useAtom } from "jotai";
import AngleDown from "../../../assets/images/dropdownAngle.svg";
import RightAngle from "../../../assets/images/rightAnglePath.svg";
import { setCurrentCommunity, setRefetchMultiCommunityList, setRefetchSpecificUser } from "../../../redux/multicommunity/multicommunity";
import { setCurrentTab } from "../../../redux/links/linksSlice";
import PlusCircle from "../../../assets/images/plus-cricle-filled-icon.svg";
import useImageUpload from "../../../hooks/useImageUpload";
import ImageUploadModals from "../../../components/ImageUploadModals/ImageUploadModals";
import useApi from "../../../helpers/apiHelper/requestHelper";
import { toastify } from "../../../helpers/toast/toastify";
import { storeUser } from "../../../redux/users/usersSlice";
import { getMemberCommunityDisplayName } from "../../../helpers/memberCommunityDisplay";

const NewSidebar = ({ items, communities, isOpen = false, onClose = () => {} }) => {
  const selectedCommunity = useSelector(
    (state) => state?.multiCommunity?.currentCommunity
  );
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [currentPageTitle, setCurrentPageTitle] = useAtom(
    globalCurrentPageTitle
  );
  const api = useApi();
  const [isCommunityDropDownOpen, serIsCommunityDropDownOpen] = useState(false);
  const previousLocationPathRef = useRef("");
  const isInitialMountRef = useRef(true);
  const sidebarLinksRef = useRef(null);
  const onBoardingStartContext = useSelector(
    (state) => state.user?.onBoardingStartContext
  );
  const [logo, setLogo] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = location.pathname;

  const user = useSelector((e) => e?.user?.user);

  // Use the centralized image upload hook
  const {
    fileInputRef,
    imageSrc,
    showCropper,
    editor,
    scale,
    // fileName,
    showEditChoiceModal,
    // isProgrammaticUpload,
    setEditor,
    setScale,
    // setFileName,
    setIsProgrammaticUpload,
    setShowEditChoiceModal,
    handleFileChange,
    handleIconClick,
    handleEditChoice,
    handleCropSave,
    handleCropCancel,
  } = useImageUpload(selectedCommunity);

  useEffect(() => {
    if (isOpen && !isSidebarOpen) {
      setIsSidebarOpen(true);
    }
  }, [isOpen]);

  const closeSidebar = () => {
    setIsClosing(true);
    onClose();
  };

  const handleLinkClick = (path) => {
    // navigate(path);
    setIsSidebarOpen(false);
    setIsClosing(false);
    onClose();
    navigate(path);
  };

  const toggleDropdown = (index) =>
    setOpenDropdown(openDropdown === index ? null : index);

  const toggleSidebar = () => {
    if (isSidebarOpen) {
      closeSidebar();
    } else {
      setIsSidebarOpen(true);
    }
  };

  useEffect(() => {
    document.body.style.overflow = (isSidebarOpen || isOpen) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isSidebarOpen, isOpen]);

  // Lock body scroll when community dropdown is open — mobile only
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isCommunityDropDownOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isCommunityDropDownOpen]);

  const handleOverlayAnimationEnd = () => {
    if (isClosing) {
      setIsClosing(false);
      setIsSidebarOpen(false);
      onClose();
    }
  };

  useEffect(() => {
    if (user?.role === "leader" && !(user?.leaderCommunity)) {
      setLogo(user?.image);
    } else if (user?.role === "leader" && (user?.leaderCommunity)) {
      setLogo(selectedCommunity?.logo);
    } else if (user?.role === "member") {
      setLogo(user?.memberCommunities[0]?.community?.logo);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        serIsCommunityDropDownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => { document.removeEventListener("mousedown", handleClickOutside); };
  }, []);

  useEffect(() => {
    if (user && items.length > 0) {
      const currentPath = location.pathname;      
      const isPathChanged = currentPath !== previousLocationPathRef.current;

      if (!isInitialMountRef.current && !isPathChanged) return;

      const isInitialMount = isInitialMountRef.current;
      isInitialMountRef.current = false;
      previousLocationPathRef.current = currentPath;

      const otherUrls = [
        {
          label: "Settings",
          currentPageTitle: "Settings",
          url: `/${user?.role}/newAccountSettings`,
        },
      ];

      const findTitleFromItems = (items, path) => {
        const mainItem = items.find((item) => item.url === path);
        if (mainItem?.currentPageTitle) return mainItem.currentPageTitle;

        for (const item of items) {
          if (item.subItems) {
            const subItem = item.subItems.find((sub) => sub.url === path);
            if (subItem?.currentPageTitle) return subItem.currentPageTitle;
          }
        }

        const otherItem = otherUrls.find((item) => item.url === path);
        if (otherItem?.currentPageTitle) return otherItem.currentPageTitle;

        return null;
      };

      let newTitle = findTitleFromItems(items, currentPath);

      if (isInitialMount && currentPageTitle !== newTitle) {
        if (currentPageTitle) setCurrentPageTitle("");
      }

      if (!newTitle && onBoardingStartContext) {
        newTitle = findTitleFromItems(items, onBoardingStartContext);
      }

      if (newTitle) setCurrentPageTitle(newTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);

  const setSelectedCommunity = async (community) => {
    try {
      const response = await api("post", "/users/setCommunityId", {
        communityId: community.leader,
        mainLeader: community.mainLeader,
      });

      if (response?.status) {
        // toastify("Community selected successfully!", "success");
        const responseSpecificUser = await api("post", "multiCommunity/getSpecificUser", {
          id: community.leader,
        });

        localStorage.setItem("token", responseSpecificUser?.data?.token);
        dispatch(storeUser(responseSpecificUser?.data));
        dispatch(setCurrentCommunity(community));
        dispatch(setRefetchSpecificUser(true));

        serIsCommunityDropDownOpen(false);
        return response.data;
      } else {
        toastify(response?.message || "Failed to update community", "error");
        return null;
      }
    } catch (error) {
      console.error("[setSelectedCommunity] ❌ Error:", error);
      toastify("Something went wrong while setting community", "error");
      return null;
    }
  };

  return (
    <>
      {/* ── Desktop sidebar ── */}
      <div className="newSidebarBox">
        <div className="communityProfileSelection">
          <div className="img-box">
            <div className="img-wrapper">
              <img src={logo || Profile} alt="" className="logo-img" />
              {((user?.role === "leader" && user.leaderCommunity && !selectedCommunity?.logo) ||
                (user?.role === "leader" && !user.image && !user?.leaderCommunity)) && (
                <>
                  <img src={PlusCircle} alt="" className="plus-icon"
                    onClick={() => { setIsProgrammaticUpload(false); handleIconClick(); }}
                  />
                  <input type="file" ref={fileInputRef} onChange={handleFileChange}
                    accept="image/jpeg,image/jpg,image/png,image/gif" style={{ display: "none" }} />
                </>
              )}
            </div>
          </div>
          <div className="communityProfileDropdown" ref={dropdownRef}>
            {user?.role === "leader" && communities?.length > 1 ? (
              <>
                <button className="dropdown-toggle"
                  onClick={() => serIsCommunityDropDownOpen(!isCommunityDropDownOpen)}>
                  <span className="communityNameText">{selectedCommunity?.disPlayName}</span>
                  <span className={`arrow ${isCommunityDropDownOpen ? "rotate" : ""}`}>
                    <img src={AngleDown} alt="Arrow Down" />
                  </span>
                </button>
                {isCommunityDropDownOpen && (
                  <div className="dropdown-menu">
                    {communities?.length > 0 && communities.map((community, index) => (
                      <div key={index}
                        className={`dropdown-item ${selectedCommunity === community ? "active" : ""}`}
                        onClick={async () => await setSelectedCommunity(community)}>
                        <p className="text">{community?.disPlayName}</p>
                        <span className="arrow"><img src={RightAngle} alt="Arrow Right" /></span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {user?.role === "leader" && (
                  <button className="dropdown-toggle">
                    <span className="communityNameText">
                      {selectedCommunity?.disPlayName || user?.tradeName || user?.mainLeader?.tradeName}
                    </span>
                  </button>
                )}
              </>
            )}
            {(user?.role === "member" || user?.role === "memberNoLink") && (
              <button className="dropdown-toggle">
                <span className="communityNameText">
                  {getMemberCommunityDisplayName(user)}
                </span>
              </button>
            )}
            {/* memberNoLink community name handled above via getMemberCommunityDisplayName */}
          </div>
        </div>
        <div className="NewsidebarLinksBox" ref={sidebarLinksRef}>
          {items?.length > 0 && items.map((item, index) => (
            <div key={index}>
              {!item.isDropDown && (
                <button
                  className={`newSidebarLink ${currentPage === item.url ? "active" : ""}`}
                  onClick={() => {
                    dispatch(setCurrentTab("subscribe"));
                    handleLinkClick(item.url);
                    toggleDropdown(openDropdown);
                    if (item.currentPageTitle) setCurrentPageTitle(item.currentPageTitle);
                  }}>
                  <span className="newSidebarLink__icon">
                    {item.icon}
                    {item.showIndicator && (
                      <span
                        className="newSidebarLink__indicator"
                        aria-label="Action needed"
                      />
                    )}
                  </span>
                  {item.label}
                </button>
              )}
              {item.isDropDown && (
                <div className="dropdown">
                  <button
                    className={`dropdownToggle ${item.subItems.some((sub) => sub.url === currentPage) ? "active" : ""}`}
                    onClick={() => toggleDropdown(index)}>
                    {item.icon}
                    {item.label}
                    <svg 
                    className={`dropdownArrow ${openDropdown === index ? "open" : ""}`}
                      xmlns="http://www.w3.org/2000/svg" width="6" height="4" viewBox="202.396 290.814 6 4">
                      <path d="m205.396 294.814-3-4h6z" data-name="Polygon 6" fillRule="evenodd" />
                    </svg>
                  </button>
                  {openDropdown === index && (
                    <div className="dropdownMenu">
                      {item.subItems.map((subItem, subIndex) => (
                        <button className="dropdownItem" key={subIndex}
                          onClick={() => {
                            setCurrentPageTitle(subItem.currentPageTitle);
                            toggleDropdown(index);
                            handleLinkClick(subItem.url);
                          }}>
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="newSidebarBottomBox">
          <span className="divider"></span>
          <p className="PoweredBy">Powered by</p>
          <img alt="" src={logo_black} />
        </div>
      </div>

      {/* Image Upload Modals */}
      <ImageUploadModals
        showCropper={showCropper}
        imageSrc={imageSrc}
        editor={editor}
        setEditor={setEditor}
        scale={scale}
        setScale={setScale}
        handleCropSave={handleCropSave}
        handleCropCancel={handleCropCancel}
        communityName={
          selectedCommunity?.disPlayName ||
          user?.tradeName ||
          user?.mainLeader?.tradeName
        }
        showEditChoiceModal={showEditChoiceModal}
        setShowEditChoiceModal={setShowEditChoiceModal}
        handleEditChoice={handleEditChoice}
        fileInputRef={fileInputRef}
        setIsProgrammaticUpload={setIsProgrammaticUpload}
      />
    </>
  );
};

export default NewSidebar;
